<template>
  <div id="app">
    <!-- Impersonation Alert -->
    <ImpersonationAlert />
    
    <!-- Global Permission Alert -->
    <b-alert
      v-if="showPermissionAlert"
      variant="warning"
      dismissible
      fade
      show
      class="permission-alert-global"
      @dismissed="hidePermissionAlert"
    >
      <h5><i class="fa fa-exclamation-triangle"></i> Permisos Insuficientes</h5>
              <p>No tienes permisos para {{ permissionAction }}.</p>
        <div v-if="rootUsers && rootUsers.root_users && rootUsers.root_users.length > 0">
          <p><strong>Contacta a una de las siguientes personas para que te de acceso:</strong></p>
          <ul class="mb-0">
            <li v-for="user in filteredRootUsers" :key="user.user_id" class="permission-user-item">
              <span class="user-info">
                {{ user.name }}{{ user.surname ? ' ' + user.surname : '' }} 
                <i 
                  class="fa fa-copy copy-email-icon" 
                  :title="`Copiar email: ${user.email}`"
                  @click="copyEmail(user.email)"
                ></i>{{ user.email }}
              </span>
            </li>
          </ul>
        </div>
      <div v-else>
        <p>Contacta al administrador del sistema.</p>
      </div>
    </b-alert>
    <router-view></router-view>
    <portal-target name="menu-portal"></portal-target>
    <portal-target name="chart-tooltip-portal"></portal-target>
    <portal-target name="ag-grid-dropdown-portal"></portal-target>
    <portal-target name="tabs-dropdown-portal"></portal-target>
    <portal-target name="news-feed-portal"></portal-target>
    <portal-target name="store-map-popup-portal"></portal-target>
    <portal-target name="route-assign-portal"></portal-target>
    <portal-target name="modal-select-portal"></portal-target>
    <portal-target name="grid-row-tooltip-portal"></portal-target>
  </div>
</template>

<script>
import { getRootUsers } from '@/app/shared/services/rootUsersService'
import { ClientLambdaApi, ClientErAdminData, ClientBackGroundTasks } from '@/api-client'
import ImpersonationAlert from '@/app/shared/components/ImpersonationAlert.vue'

export default {
  name: 'app',
  components: {
    ImpersonationAlert
  },
  data () {
    return {
      showPermissionAlert: false,
      permissionAction: 'realizar esta acción'
    }
  },
  async created () {
    // Setup global 403 interceptor function
    const vm = this
    const handle403Error = async (error) => {
      if (error.response && error.response.status === 403) {
        
        // Map the current route to a user-friendly action
        const routeToActionMap = {
          '/admin/users-management': 'gestionar usuarios',
          '/admin/users-management/user': 'editar usuario',  
          '/admin/users-management/create': 'crear usuario',
          '/admin/users': 'gestionar usuarios',
          '/admin/roles': 'gestionar roles',
          '/admin/permissions': 'gestionar permisos'
        }
        
        const currentPath = vm.$route?.path || window.location.pathname
        vm.permissionAction = routeToActionMap[currentPath] || 'realizar esta acción'
        
        // Root users are already loaded in store via getters
        
        vm.showPermissionAlert = true
        
        // Auto-hide after 10 seconds
        setTimeout(() => {
          vm.showPermissionAlert = false
        }, 10000)
      }
      
      return Promise.reject(error)
    }
    
    // Add interceptor to all API clients
    const apiClients = [ClientLambdaApi, ClientErAdminData, ClientBackGroundTasks]
    apiClients.forEach((client, index) => {
      client.interceptors.response.use(
        response => response,
        handle403Error
      )
    })
  },
  methods: {
    hidePermissionAlert () {
      this.showPermissionAlert = false
    },
    async copyEmail (email) {
      try {
        await navigator.clipboard.writeText(email)
        // Optional: Show brief feedback that email was copied
        // You could add a toast notification here if desired
      } catch (error) {
        // Fallback for older browsers
        this.fallbackCopyEmail(email)
      }
    },
    fallbackCopyEmail (email) {
      // Fallback method for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = email
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
      } catch (error) {
        // Fallback copy failed
      }
      document.body.removeChild(textArea)
    }
  },
  computed: {
    rootUsers () {
      return this.$store.getters.rootUsers || []
    },
    filteredRootUsers () {
      if (!this.rootUsers || !this.rootUsers.root_users) {
        return []
      }
      // Filter out users with root_type "DOMAIN_BASED"
      return this.rootUsers.root_users.filter(user => user.root_type !== 'DOMAIN_BASED')
    }
  }
}
</script>

<style>
  /* Import Bootstrap Vue Styles */
  @import '~bootstrap-vue/dist/bootstrap-vue.css';
  @import '~bootstrap/dist/css/bootstrap.css';
  /* Import Font Awesome Icons Set */
  @import '~font-awesome/css/font-awesome.min.css';
  /* Import Simple Line Icons Set */
  /* @import '~simple-line-icons/css/simple-line-icons.css'; */
  /* Estilo de los ag grid por defecto */
  /* @import '~ag-grid/dist/styles/ag-grid.css'; */
  /* Tema de estilo para ag grid */
  /* @import '~ag-grid/dist/styles/theme-fresh.css'; */
  /* @import '~ag-grid/dist/styles/ag-theme-balham.css'; */
</style>
<style lang="scss">
  // Font paths
  $fa-font-path: '~font-awesome/fonts/';
  $simple-line-font-path: '~simple-line-icons/fonts/';
  
  // Import Main styles for this application
  @import './scss/style';
  @import "../node_modules/ag-grid-community/dist/styles/ag-grid.css";
  @import "../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";
  @import  "../node_modules/vue-multiselect/dist/vue-multiselect.min.css";
  @import "../node_modules/vue-loading-overlay/dist/vue-loading.css";

  .ag-icon {
    color: #fffdfd !important;
  }

  .permission-alert-global {
    margin: 1rem;
    border-left: 4px solid #f0ad4e;
    position: fixed;
    top: 20px;
    right: 20px;  
    left: 20px;
    z-index: 9999;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .permission-user-item {
    padding: 0.25rem 0;
  }

  .user-info {
    font-weight: 500;
  }

  .copy-email-icon {
    margin-left: 0.5rem;
    margin-right: 0.25rem;
    cursor: pointer;
    color: #6c757d;
    transition: all 0.2s ease;
    padding: 0.125rem;
  }

  .copy-email-icon:hover {
    color: #007bff;
    transform: scale(1.1);
  }

  .contact-links {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .alert-link {
    font-weight: 500;
    text-decoration: none;
    padding: 0.25rem 0.5rem;
    border-radius: 0.375rem;
    background-color: rgba(255, 193, 7, 0.1);
    transition: all 0.2s ease;
  }

  .alert-link:hover {
    background-color: rgba(255, 193, 7, 0.2);
    text-decoration: none;
    transform: translateY(-1px);
  }
</style>



// WEBPACK FOOTER //
// src/App.vue